import { IonList, IonSearchbar } from '@ionic/react';
import { PlotItem, SearchToolbar } from '@lidea/search/mobile/shared/ui';
import { FiltersModal } from '@lidea/search/shared/feature-filters-modal';
import {
  useFilterStore,
  useSelectedFilters,
} from '@lidea/shared/data-access/core';
import { useInfinitePlots } from '@lidea/shared/data-access/plots';
import { useOfflineAwarePlotFilters } from '@lidea/shared/feature-offline';
import { MobilePage } from '@lidea/shared/ui/layout';
import { ZoningStatus } from '@lidea/shared/util/types';
import {
  useDebouncedState,
  useDisclosure,
  useNetwork,
} from '@mantine/hooks';
import { useVirtualizer } from '@tanstack/react-virtual';
import classNames from 'classnames';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './list-tab.module.css';

interface FooterProps {
  hasNextPage: boolean;
}
const Footer = (props: FooterProps) => {
  const { t } = useTranslation(['plot'], { useSuspense: false });
  return (
    <div
      style={{
        padding: '2rem',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      {props.hasNextPage ? (
        <>{t('plot:loading_plots')}</>
      ) : (
        <>{t('plot:nothing_load')}</>
      )}
    </div>
  );
};

/* eslint-disable-next-line */
export interface ListTabProps {}

export function ListTab(props: ListTabProps) {
  const { i18n } = useTranslation(['plot'], { useSuspense: false });
  const virtualListParentRef = useRef<HTMLIonListElement>(null);
  const [isModalOpen, modalHandlers] = useDisclosure(false);
  const networkStatus = useNetwork();

  const [searchQuery, setSearchQuery] = useDebouncedState('', 300);

  const selectedFilters = useFilterStore((state) => state.selectedFilters);
  const plotFilters = useOfflineAwarePlotFilters(selectedFilters);
  const setSearchQueryFilter = useFilterStore((state) => state.setSearchQuery);
  const resetFilters = useFilterStore((state) => state.resetFilters);
  const { selectedFiltersCount } = useSelectedFilters();

  const infinitePlots = useInfinitePlots({
    selectedFilters: plotFilters,
  });

  const locale = i18n.language as 'fr' | 'en';

  useEffect(() => {
    if (networkStatus.online) {
      setSearchQueryFilter(searchQuery);
    }
  }, [searchQuery, networkStatus.online, setSearchQueryFilter]);

  type IonSearchbarProps = React.ComponentProps<typeof IonSearchbar>;
  const handleSearchChange: IonSearchbarProps['onIonInput'] = (ev) => {
    const value = ev.detail.value ?? '';
    setSearchQuery(value.trim());
  };

  const allRows = infinitePlots.data
    ? infinitePlots.data.pages
        .flatMap((d) => d.results.features)
        // filter by name when offline
        .filter((row) =>
          !networkStatus.online
            ? row.properties.name
                .toLowerCase()
                .includes(searchQuery.toLowerCase().trim())
            : true
        )
    : [];

  const rowVirtualizer = useVirtualizer({
    count: allRows.length + 1,
    getScrollElement: () => virtualListParentRef.current,
    estimateSize: () => 100,
    overscan: 5,
    enabled: true,
  });

  useEffect(() => {
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

    if (!lastItem) {
      return;
    }

    if (
      lastItem.index >= allRows.length - 1 &&
      infinitePlots.hasNextPage &&
      !infinitePlots.isFetchingNextPage &&
      networkStatus.online
    ) {
      infinitePlots.fetchNextPage();
    }
  }, [
    infinitePlots.hasNextPage,
    infinitePlots.fetchNextPage,
    allRows.length,
    infinitePlots.isFetchingNextPage,
    rowVirtualizer.getVirtualItems(),
    networkStatus.online,
  ]);

  useEffect(() => {
    if (!networkStatus.online) {
      resetFilters();
    }
  }, [networkStatus.online]);

  return (
    <MobilePage
      headerExtension={
        <SearchToolbar
          filtersCount={selectedFiltersCount}
          onFilterClick={modalHandlers.open}
          onSearchTermChange={handleSearchChange}
        />
      }
    >
      <IonList
        ref={virtualListParentRef}
        className={classNames(styles['plot-list'], 'ion-padding')}
      >
        <div
          style={{
            height: rowVirtualizer.getTotalSize(),
            width: '100%',
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${
                rowVirtualizer.getVirtualItems()[0]?.start ?? 0
              }px)`,
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const isLoaderRow = virtualRow.index > allRows.length - 1;
              const plot = allRows[virtualRow.index];

              return (
                <div
                  key={virtualRow.key as string}
                  ref={rowVirtualizer.measureElement}
                  data-index={virtualRow.index}
                  style={{
                    paddingBottom: '16px',
                  }}
                >
                  {isLoaderRow ? (
                    <Footer
                      hasNextPage={
                        networkStatus.online && infinitePlots.hasNextPage
                      }
                    />
                  ) : (
                    <PlotItem
                      key={plot.id}
                      area={plot.properties.area}
                      id={plot.id as number}
                      name={plot.properties.name}
                      ownerName={`${plot.properties.farm.name} - ${
                        plot.properties.variety[`name_${locale}`]
                      }`}
                      species={plot.properties.species[`name_${locale}`]}
                      yieldStatus={
                        plot.properties.analyzes[0]?.status ||
                        ZoningStatus.ToBeDone
                      }
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </IonList>

      <FiltersModal
        isOpen={isModalOpen}
        modalClassName="page-modal"
        onWillDismiss={modalHandlers.close}
      />
    </MobilePage>
  );
}

export default ListTab;
